import React from 'react';
import { Bot } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { ConversationItem, clockTime } from './conversation';
import ChatMarkdown from '../../chat/ChatMarkdown';

// ChatMarkdown is styled for the dark site widget; recolour its bold text,
// list markers, code and links for the light admin bubble.
const AI_MARKDOWN =
  '[&_strong]:text-[color:var(--oa-text)] [&_li>span:first-child]:text-[color:var(--oa-muted)] [&_code]:bg-[color:color-mix(in_oklab,var(--oa-pill-bg)_5%,transparent)] [&_a]:text-[color:var(--oa-accent)] [&_a]:decoration-[color:color-mix(in_oklab,var(--oa-accent)_40%,transparent)]';

/** Renders one grouped run of consecutive messages from the same sender. */
export const MessageGroupView: React.FC<{ item: Extract<ConversationItem, { kind: 'group' }> }> = ({ item }) => {
  if (item.sender_type === 'system') {
    return (
      <div className="flex flex-col items-center gap-1.5 py-1">
        {item.messages.map((m) => (
          <span key={m.id} className="rounded-full bg-[color:var(--oa-raised-2)] px-3 py-1 text-[11.5px] text-[color:var(--oa-muted)]">
            {m.content}
          </span>
        ))}
      </div>
    );
  }

  const isAdmin = item.sender_type === 'admin';
  const isAi = item.sender_type === 'ai';
  const label = isAdmin ? item.admin_name || 'You' : isAi ? 'AI assistant' : 'Visitor';
  const last = item.messages[item.messages.length - 1];

  return (
    <div className={cn('flex flex-col gap-1', isAdmin ? 'items-end' : 'items-start')}>
      <span className={cn('flex items-center gap-1 px-0.5 text-[11px] font-medium text-[color:var(--oa-muted)]', isAdmin && 'flex-row-reverse')}>
        {isAi ? <Bot className="h-3 w-3" /> : null}
        {label}
      </span>

      <div className="flex max-w-[80%] flex-col gap-1">
        {item.messages.map((m, i) => {
          const isLast = i === item.messages.length - 1;
          return (
            <div
              key={m.id}
              className={cn(
                'rounded-2xl px-3.5 py-2 text-[13.5px]',
                isAdmin
                  ? cn('whitespace-pre-wrap bg-[color:var(--oa-pill-bg)] text-[color:var(--oa-pill-text)]', isLast && 'rounded-br-md')
                  : isAi
                  ? cn(AI_MARKDOWN, 'bg-[color:var(--oa-accent-soft)] text-[color:var(--oa-text)]', isLast && 'rounded-bl-md')
                  : cn('whitespace-pre-wrap border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] text-[color:var(--oa-text)]', isLast && 'rounded-bl-md')
              )}
            >
              {isAi ? <ChatMarkdown text={m.content} /> : m.content}
            </div>
          );
        })}
      </div>

      <span className="px-0.5 text-[11px] text-[color:var(--oa-muted)]">{clockTime(last.created_at)}</span>
    </div>
  );
};
